/**
 * Who may open a database or dashboard, and whether AI may.
 *
 * The same shape as the table access dialog it follows -- the workspace's
 * members with a switch each, written as a whole set -- one level up, where
 * closing something takes its tables, views, widgets and rows with it.
 *
 * Two things the endpoint decides and this only reports. The creator and the
 * workspace admins are not on the list and cannot be taken off it: they can
 * open the application whatever it says. And the list means nothing until the
 * application is restricted, so it is disabled rather than hidden while it is
 * off -- somebody can see who would be allowed before allowing anybody.
 *
 * `MCP access` is a separate axis rather than another member on the list: it
 * closes the channel, not the data, so a closed application stays open to every
 * person who could already open it.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { memberLabel, sortMembers } from '@/features/workspace/members'
import { useWorkspace } from '@/features/workspace/queries'
import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { Label } from '@/shared/ui/label'
import { Skeleton } from '@/shared/ui/skeleton'
import { Switch } from '@/shared/ui/switch'
import { useApplicationAccess, useSetApplicationAccess } from './queries'
import { AccessSection, McpAccessSection } from './sections'

export function ApplicationAccessDialog({
  workspaceId,
  applicationId,
  applicationName,
  onClose,
}: {
  workspaceId: number
  applicationId: number
  applicationName: string
  onClose: () => void
}) {
  const { t } = useTranslation()
  const workspace = useWorkspace(workspaceId)
  const access = useApplicationAccess(applicationId)
  const save = useSetApplicationAccess(workspaceId, applicationId)

  const [restricted, setRestricted] = useState<boolean | null>(null)
  const [mcpAccess, setMcpAccess] = useState(true)
  const [allowed, setAllowed] = useState<number[]>([])

  // Filled once the stored answer has arrived, and only then: starting from
  // "not restricted" and correcting it later would say the application is open
  // to everybody for as long as the request took, which is the opposite of what
  // somebody opened this to check.
  useEffect(() => {
    if (access.data === undefined || restricted !== null) return
    setRestricted(access.data.restrict_access)
    setMcpAccess(access.data.mcp_access)
    setAllowed(access.data.allowed_user_ids)
  }, [access.data, restricted])

  const members = sortMembers(workspace.data?.users ?? [])

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('applicationAccess.title')}</DialogTitle>
          <DialogDescription>
            {t('applicationAccess.description', { application: applicationName })}
          </DialogDescription>
        </DialogHeader>

        {restricted === null ? (
          <Skeleton className="h-32" />
        ) : (
          <>
            <AccessSection title={t('memberAccess.title')}>
              <div className="flex items-center justify-between rounded border p-2">
                <Label
                  htmlFor="application-access-restrict"
                  className="text-sm font-normal"
                >
                  {t('applicationAccess.restrict')}
                </Label>
                <Switch
                  id="application-access-restrict"
                  checked={restricted}
                  onCheckedChange={setRestricted}
                />
              </div>

              <ul
                aria-label={t('applicationAccess.allowed')}
                className="flex max-h-60 flex-col gap-2 overflow-y-auto"
              >
                {members.map((member) => (
                  <li key={member.user_id} className="flex items-center justify-between">
                    <Label
                      htmlFor={`application-access-${member.user_id}`}
                      className="text-sm font-normal"
                    >
                      {memberLabel(member)}
                    </Label>
                    <Switch
                      id={`application-access-${member.user_id}`}
                      disabled={!restricted}
                      checked={allowed.includes(member.user_id)}
                      onCheckedChange={(on) =>
                        setAllowed(
                          on
                            ? [...allowed, member.user_id]
                            : allowed.filter((id) => id !== member.user_id),
                        )
                      }
                    />
                  </li>
                ))}
              </ul>
            </AccessSection>

            <McpAccessSection
              id="application-access-mcp"
              description={t('applicationAccess.mcpDescription')}
              checked={mcpAccess}
              onCheckedChange={setMcpAccess}
            />

            <DialogFooter>
              <Button variant="ghost" onClick={onClose}>
                {t('action.cancel')}
              </Button>
              <Button
                disabled={save.isPending}
                onClick={() => {
                  save.mutate(
                    {
                      restrict_access: restricted,
                      mcp_access: mcpAccess,
                      allowed_user_ids: allowed,
                    },
                    { onSuccess: onClose },
                  )
                }}
              >
                {t('action.save')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
